import { useEffect, useState } from 'react';
import PartCategoryService from 'services/part-category-service';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tooltip from '@mui/material/Tooltip';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import ArrowUpwardTwoToneIcon from '@mui/icons-material/ArrowUpwardTwoTone';
import ArrowDownwardTwoToneIcon from '@mui/icons-material/ArrowDownwardTwoTone';
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';

import AttributeDialog from './attributeDialog';
import MainAttributeDialog from './mainAttributeDialog';
import ConfirmDeleteDialog from './confirmDeleteDialog';

const AttributeFields = ({
    id,
    numAttributes,
    setNumAttributes,
    changeHandler,
    editMode,
    attributesData,
    attributes,
    setAttributes,
    setOrderChanged }) => {
    // States to manage the dialogs for attributes
    const [openAttributeDialog, setOpenAttributeDialog] = useState(false);
    const [openMainAttributeDialog, setOpenMainAttributeDialog] = useState(false);
    const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
    const [attNum, setAttNum] = useState(0);
    const [selectorValues, setSelectorValues] = useState({});
    const [deleteValue, setDeleteValue] = useState("");
    const [attributeFields, setAttributeFields] = useState([]);

    const [loading, setLoading] = useState(true);

    // States to manage attributes and their preview fields
    const [attribute, setAttribute] = useState({})

    // Reload fields when attributes change
    useEffect(() => {
        setAttributeFields(renderAttributeFields());
    }, [selectorValues])

    useEffect(() => {
        if (editMode && attributesData) {
            setAttributes(attributesData);
        }
        setLoading(false);
    }, [])

    // Hook to sync with parent component
    useEffect(() => {
        changeHandler({ target: { name: 'attributes', value: attributes } });
        setAttributeFields(renderAttributeFields());
    }, [attributes]);

    // Loading used to re-render the attribute fields after deletion or addition.
    useEffect(() => {
        setLoading(true);
        setAttributeFields(renderAttributeFields());
        setTimeout(() => {
            setLoading(false);
        }, 400);
    }, [numAttributes])

    // Handles opening the dialog for attribute editing
    const handleOpenDialog = (num) => {
        setAttNum(num);


        const baseAttributes = {
            attribute: attributes[num]?.attribute || '',
            options: attributes[num]?.options || '',
            description: attributes[num]?.description || '',
            units: attributes[num]?.units || '',
            hintText: attributes[num]?.hintText || '',
        };

        // Open the appropriate dialog with , either regular attribute or main attribute
        if (num === 0) {
            setAttribute({
                attribute: "TYPE",
                ...attributes[num]
            });
            setOpenMainAttributeDialog(true);
        } else {
            setAttribute({
                ...baseAttributes,
                enableMultiVals: attributes[num]?.enableMultiVals || false
            })
            setOpenAttributeDialog(true);
        }
    }

    // Handles closing the dialog and updating the attributes state
    const handleCloseDialog = (attribute, num) => {
        setOpenAttributeDialog(false);
        setOpenMainAttributeDialog(false);

        // Validate that num is a number and attribute is an object before setting
        if (typeof num === 'number' && attribute && typeof attribute === 'object') {
            setAttributes({
                ...attributes,
                [num]: attribute
            })
        }
        if (num > 0) {
            if (attribute.enableMultiVals) {
                setSelectorValues({
                    ...selectorValues,
                    [num]: []
                })
            } else {
                setSelectorValues({
                    ...selectorValues,
                    [num]: ""
                })
            }
        }
        setAttribute({}); // Reset attribute state after closing dialog
    }

    // Handles opening the confirm delete dialog
    const handleOpenConfirmDeleteDialog = (num) => {
        setAttNum(num);
        setDeleteValue(attributes[num]?.attribute || '');
        setOpenConfirmDeleteDialog(true);
    }

    // Handles closing the confirm delete dialog and updating the attributes state.
    const handleCloseDeleteDialog = (deletedNum) => {
        setOpenConfirmDeleteDialog(false);
        setLoading(true);
        let updatedAttributes = { ...attributes };
        delete updatedAttributes[deletedNum];

        if (deletedNum > 0) {
            if (deletedNum < numAttributes) {
                updatedAttributes = rearangeAttributes(updatedAttributes);
            }
            if (attributes[0]?.enableCondAttrs) {
                const options = updatedAttributes[0].options.split(';');
                // Removes the attribute from conditional options in which it was featured
                options.forEach(opt => {
                    if (updatedAttributes[0][opt.trim()]) {
                        let nums = updatedAttributes[0][opt.trim()].split(';');
                        if (nums.includes((numAttributes - 1).toString())) {
                            nums = nums.filter(num => num !== (numAttributes - 1).toString());
                            updatedAttributes[0][opt.trim()] = nums.join(';');
                        }
                    }
                });
            }
            setNumAttributes(numAttributes - 1);
        }
        setAttributes(updatedAttributes);

        setOpenConfirmDeleteDialog(false);
        setLoading(false);
    }

    // Rearranges the attributes after deletion to maintain the correct order.
    // This function is used to ensure that the attributes are in the correct order after deletion.
    const rearangeAttributes = (updatedAttributes) => {
        const rearangedAtts = {};
        let newIndex = updatedAttributes[0] ? 0 : 1;

        // Iterate through all existing attributes
        for (let i = updatedAttributes[0] ? 0 : 1; i <= Object.keys(attributes).length; i++) {
            // If this attribute exists, add it to updatedAttributes with a new index
            if (updatedAttributes[i]) {
                rearangedAtts[newIndex] = attributes[i];
                newIndex++;
            }
        }

        return rearangedAtts;
    }

    // Helper to swap conditional attribute references in the main (TYPE) attribute
    function swapConditionalRefs(updatedAttributes, a, b) {
        if (!updatedAttributes[0]?.enableCondAttrs) return;

        const optsStr = updatedAttributes[0].options || '';
        const options = optsStr.split(';').map(o => o.trim()).filter(Boolean);

        const aStr = String(a);
        const bStr = String(b);

        options.forEach(opt => {
            const key = opt.trim();
            if (!key) return;
            const val = updatedAttributes[0][key];
            if (!val) return;

            const nums = val.split(';').map(n => n.trim()).filter(Boolean);
            const swapped = nums.map(n => {
                if (n === aStr) return bStr;
                if (n === bStr) return aStr;
                return n;
            });

            updatedAttributes[0][key] = swapped.join(';');
        });
    }

    // Function used to swap attributes order 
    const swapAttributes = async (index, direction) => {
        let updatedAttributes = null;
        if (direction === "up") {
            if (index > 1) {
                updatedAttributes = { ...attributes };
                [updatedAttributes[index - 1], updatedAttributes[index]] = [updatedAttributes[index], updatedAttributes[index - 1]];

                const updatedSelectorValues = { ...selectorValues };
                [updatedSelectorValues[index - 1], updatedSelectorValues[index]] = [updatedSelectorValues[index], updatedSelectorValues[index - 1]];

                // After swapping attributes and selector values, update any conditional rules
                swapConditionalRefs(updatedAttributes, index, index - 1);

                setAttributes(updatedAttributes);
                setSelectorValues(updatedSelectorValues);
                setOrderChanged(true);
            }
        } else if (direction === "down") {
            if (index < numAttributes - 1) {
                updatedAttributes = { ...attributes };
                [updatedAttributes[index + 1], updatedAttributes[index]] = [updatedAttributes[index], updatedAttributes[index + 1]];

                const updatedSelectorValues = { ...selectorValues };
                [updatedSelectorValues[index + 1], updatedSelectorValues[index]] = [updatedSelectorValues[index], updatedSelectorValues[index + 1]];

                swapConditionalRefs(updatedAttributes, index, index + 1);

                setSelectorValues(updatedSelectorValues);
                setAttributes(updatedAttributes);
                setOrderChanged(true);
            }
        }
        if (editMode && updatedAttributes) {
            const payload = {
                data: {
                    type: "partCategory",
                    id: id,
                    attributes: {
                        reorderedAttributes: updatedAttributes
                    }
                }
            }
            await PartCategoryService.updateAttributesOrder(payload, id);
        }

    }

    const renderAttributeFields = () => {
        const fields = [];
        for (let i = 0; i < numAttributes; i++) {
            fields.push(
                <MDBox mt={2}>
                    <Card backgroundColor="gray-100" mt={2} p={2}>

                        <CardContent>

                            {/* Card Title with Edit and Delete Icons */}
                            <MDBox display="flex" justifyContent="space-between">

                                {/* Title */}
                                {i === 0 ?
                                    (<MDTypography variant="h6" fontWeight="bold" mb={1}>
                                        Type Field Preview:
                                    </MDTypography>) : (
                                        <MDTypography variant="h6" fontWeight="bold" mb={1}>
                                            Attribute No.{i} Preview:
                                        </MDTypography>
                                    )}

                                {/*Card Action Buttons */}
                                <MDBox>
                                    {/* Move Attribute Up Button */}
                                    {i > 1 && (
                                        <Tooltip title="Move Up" onClick={() => swapAttributes(i, "up")}>
                                            <IconButton>
                                                <ArrowUpwardTwoToneIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {/* Move Attribute Down Butotn */}
                                    {((i > 0) && (i < numAttributes - 1)) && (
                                        <Tooltip title="Move Down" onClick={() => swapAttributes(i, "down")}>
                                            <IconButton>
                                                <ArrowDownwardTwoToneIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {/* Edit button */}
                                    <Tooltip title="Edit Attribute">
                                        <IconButton onClick={() => handleOpenDialog(i)}>
                                            <EditTwoToneIcon />
                                        </IconButton>
                                    </Tooltip>
                                    {/* Delete button */}
                                    <Tooltip title="Delete Attribute">
                                        <IconButton onClick={() => handleOpenConfirmDeleteDialog(i)}>
                                            <DeleteTwoToneIcon />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>

                            </MDBox>

                            {/* Attribute Preview Fields*/}
                            <MDBox justifyContent="center" display="flex">
                                {attributes[i]?.fieldType ? (
                                    <Grid container spacing={1} justifyContent="center">
                                        {/* Text Field */}
                                        {attributes[i].fieldType === "text" && (
                                            <Grid item xs={12}>
                                                <MDBox m={1}>
                                                    <MDInput
                                                        label={attributes[i]?.attribute || "No Attribute Defined"}
                                                        fullWidth
                                                    />
                                                </MDBox>
                                            </Grid>
                                        )}
                                        {attributes[i].fieldType === "text-with-units" && (
                                            <>
                                                <Grid item xs={10}>
                                                    <FormControl sx={{ m: 1, width: '100%' }}>
                                                        <TextField
                                                            label={attributes[i]?.attribute}
                                                            fullWidth
                                                            sx={{ '& .MuiInputBase-root': { height: '45px' } }}
                                                            variant="outlined"
                                                            InputProps={{
                                                                endAdornment: attributes[i]?.units
                                                                    ? <InputAdornment position="end">{selectorValues[i]?.unit || ""}</InputAdornment>
                                                                    : null,
                                                            }}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <FormControl sx={{ m: 1, width: '100%' }}>
                                                        <InputLabel>Unit</InputLabel>
                                                        <Select
                                                            value={selectorValues[i]?.unit || ""}
                                                            onChange={e => {
                                                                setSelectorValues({
                                                                    ...selectorValues,
                                                                    [i]: {
                                                                        ...selectorValues[i],
                                                                        unit: e.target.value
                                                                    }
                                                                });
                                                            }}
                                                            label="Unit"
                                                            sx={{ height: '45px', maxWidth: '88%', '& .MuiSelect-select': { paddingY: '8px' } }}
                                                        >
                                                            {attributes[i]?.units?.split(';').map((unit, idx) => (
                                                                <MenuItem key={idx} value={unit.trim()}>
                                                                    {unit.trim()}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </>
                                        )}
                                        {attributes[i].fieldType === "select" && (
                                            <Grid item xs={12}>
                                                <MDBox mr={2}>
                                                    <FormControl sx={{ m: 1, width: '100%' }}>
                                                        <InputLabel id={`select-${i}`} >{attributes[i]?.attribute}</InputLabel>
                                                        <Select
                                                            labelId={`select-${i}`}
                                                            multiple={attributes[i]?.enableMultiVals || false}
                                                            input={<OutlinedInput label={attributes[i]?.attribute} />}
                                                            value={selectorValues[i] || []}
                                                            onChange={(e) => {
                                                                setSelectorValues({
                                                                    ...selectorValues,
                                                                    [i]: e.target.value
                                                                });
                                                            }}
                                                            fullWidth
                                                            helperText="Please select an option"
                                                            sx={{ height: '45px', '& .MuiSelect-select': { paddingY: '8px' } }}
                                                        >
                                                            {attributes[i]?.options?.split(';').map((option, idx) => (
                                                                <MenuItem key={idx} value={option.trim()}>
                                                                    {option.trim() + " - " + attributes[i]?.description.split(';')[idx]}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </MDBox>
                                            </Grid>
                                        )}
                                    </Grid>
                                ) : (
                                    <MDTypography variant="body2" fontWeight="light" mb={1}>
                                        Click on the edit icon to define this attribute.
                                    </MDTypography>
                                )}
                            </MDBox>
                        </CardContent>

                    </Card>
                </MDBox>
            );
        }
        return fields;
    };

    return (
        <>
            <AttributeDialog
                id={editMode ? id : null}
                handleClose={handleCloseDialog}
                open={openAttributeDialog}
                attNum={attNum}
                attributeData={attribute}
                existingAttributes={attributes}
                editMode={editMode}
            />
            <MainAttributeDialog
                id={editMode ? id : null}
                handleClose={handleCloseDialog}
                open={openMainAttributeDialog}
                attNum={attNum}
                attributeData={attribute}
                attributes={attributes}
                editMode={editMode}
                numberOfAttributes={numAttributes}
            />
            <ConfirmDeleteDialog
                open={openConfirmDeleteDialog}
                handleClose={handleCloseDeleteDialog}
                attNum={attNum}
                value={deleteValue}
                editMode={editMode}
                id={editMode ? id : null}
                attributes={attributes}
                setAttributes={setAttributes}
            />
            <MDBox>
                <MDTypography variant="h5" fontWeight="bold" mt={2}>
                    Define Type :
                </MDTypography>
            </MDBox>
            {attributeFields[0]}
            {loading ? (
                <MDTypography variant="body2" fontWeight="light" mb={1}>
                    Loading attributes...
                </MDTypography>
            ) : (
                <MDBox
                    display="flex"
                    flexDirection="column"
                    fullWidth
                    marginBottom="1rem"
                >
                    {numAttributes > 1 && (
                        <MDBox>
                            <MDTypography variant="h5" fontWeight="bold" mt={2}>
                                Define Attributes :
                            </MDTypography>
                        </MDBox>
                    )}
                    {attributeFields.slice(1)}
                </MDBox>)}
        </>
    );
};

export default AttributeFields;